/**
 * ReassignAccountDialog — Phase 38 "Reassign to another account" action for
 * the Member profile.
 *
 * Client for the money-critical `POST /contacts/:id/reassign` (38-04): one
 * audited transaction that re-parents the contact + their ACTIVE
 * memberships (+ any Held security deposit riding along with a moved
 * membership) to a different account. Already-issued invoices/receipts and
 * past attendance stay on the OLD account.
 *
 * This component faithfully mirrors every server guard:
 *  - 409 `pending_dues` → blocked with a clear "settle dues first" message
 *    (dialog stays open).
 *  - ALWAYS-both Primary+Billing guard: when the moved contact currently
 *    holds `primary` and/or `billing` on the OLD account, BOTH a
 *    replacement Primary AND a replacement Billing contact (drawn from the
 *    old account's OTHER contacts) must be designated before Confirm even
 *    enables client-side — they may be the same contact.
 *  - Single-contact (Individual) accounts with no spare contact render a
 *    first-class blocked state instead of a confusing empty picker
 *    (Pitfall 7 / server's `no_eligible_replacement`).
 *
 * Standalone component — 38-09 mounts it behind the profile's Reassign
 * action. Not wired into member-profile.tsx / account-detail.tsx here.
 */
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useReassignContact,
  useListAccounts,
  useListAccountContacts,
  getGetContactQueryKey,
  getGetContactStatsQueryKey,
  getGetContactTransactionsQueryKey,
  getGetAccountPlanScheduleQueryKey,
  getListAccountMembershipsQueryKey,
  getListAccountContactsQueryKey,
  type Account,
  type Contact,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, ArrowRightLeft, Loader2 } from "lucide-react";

interface ReassignFormValues {
  targetAccountId: string;
  replacementPrimaryContactId: string;
  replacementBillingContactId: string;
}

const EMPTY_VALUES: ReassignFormValues = {
  targetAccountId: "",
  replacementPrimaryContactId: "",
  replacementBillingContactId: "",
};

interface ReassignApiErrorData {
  code?: string;
  amount?: number;
  oldestDueDate?: string;
}

interface Props {
  contactId: number;
  /** The contact's CURRENT (old) account id — replacement pickers + guard
   *  are scoped to this account's other contacts. */
  currentAccountId: number;
  /** The contact's current roles on the old account — drives
   *  requiresReplacement (primary and/or billing). */
  currentRoles: readonly string[];
  open: boolean;
  onClose: () => void;
}

export function ReassignAccountDialog({
  contactId,
  currentAccountId,
  currentRoles,
  open,
  onClose,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const reassign = useReassignContact();

  const requiresReplacement =
    currentRoles.includes("primary") || currentRoles.includes("billing");

  const form = useForm<ReassignFormValues>({ defaultValues: EMPTY_VALUES });

  // The dialog instance is reused across opens — re-seed a blank form every
  // time it opens (mirrors accounts/contacts-list.tsx's ContactDialog reset
  // pattern) so a prior attempt's selections never leak into the next.
  useEffect(() => {
    if (open) form.reset(EMPTY_VALUES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId]);

  // ── Target-account picker ──────────────────────────────────────────────
  // useListAccounts() is already space-scoped server-side; render active
  // accounts minus the contact's current account (merge-dialog.tsx
  // precedent: status:"active" + pageSize:200 to cover the whole space in
  // one page for a picker).
  const { data: allAccounts, isLoading: accountsLoading } = useListAccounts(
    { status: "active", pageSize: 200 },
    {
      query: {
        enabled: open,
        queryKey: ["/api/accounts", { status: "active", pageSize: 200 }],
      },
    },
  );
  const targetOptions = (allAccounts ?? []).filter(
    (a: Account) => a.id !== currentAccountId,
  );

  // ── Replacement pickers (OLD account's OTHER contacts) ─────────────────
  // Only fetched when the guard is actually live — an account the moved
  // contact merely has "user" on never needs this list.
  const { data: oldAccountContacts, isLoading: contactsLoading } =
    useListAccountContacts(currentAccountId, {
      query: {
        enabled: open && requiresReplacement,
        queryKey: getListAccountContactsQueryKey(currentAccountId),
      },
    });
  const eligibleReplacements = (oldAccountContacts ?? []).filter(
    (c: Contact) => c.id !== contactId && c.status !== "exited",
  );
  // Pitfall 7: an Individual (single-contact) account has no spare contact
  // once the moved contact is excluded — first-class blocked state, not an
  // empty <Select>.
  const noEligibleReplacement =
    requiresReplacement && !contactsLoading && eligibleReplacements.length === 0;

  const targetAccountId = form.watch("targetAccountId");
  const replacementPrimaryContactId = form.watch("replacementPrimaryContactId");
  const replacementBillingContactId = form.watch("replacementBillingContactId");

  // Defense-in-depth over the server's 422s: Confirm never enables until the
  // client-visible precondition set is fully satisfied.
  const canSubmit =
    !!targetAccountId &&
    !noEligibleReplacement &&
    (!requiresReplacement ||
      (!!replacementPrimaryContactId && !!replacementBillingContactId));

  function invalidateProfileQueries(oldAccountId: number, newAccountId: number) {
    // Profile: contact record + stat tiles + transactions (contactId-keyed).
    queryClient.invalidateQueries({ queryKey: getGetContactQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getGetContactStatsQueryKey(contactId) });
    queryClient.invalidateQueries({
      queryKey: getGetContactTransactionsQueryKey(contactId),
    });
    // Plan-schedule + memberships + contacts lists on BOTH sides of the move.
    queryClient.invalidateQueries({
      queryKey: getGetAccountPlanScheduleQueryKey(oldAccountId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetAccountPlanScheduleQueryKey(newAccountId),
    });
    queryClient.invalidateQueries({
      queryKey: getListAccountMembershipsQueryKey(oldAccountId),
    });
    queryClient.invalidateQueries({
      queryKey: getListAccountMembershipsQueryKey(newAccountId),
    });
    queryClient.invalidateQueries({
      queryKey: getListAccountContactsQueryKey(oldAccountId),
    });
    queryClient.invalidateQueries({
      queryKey: getListAccountContactsQueryKey(newAccountId),
    });
  }

  async function onSubmit(values: ReassignFormValues) {
    if (!canSubmit) return;
    try {
      const result = await reassign.mutateAsync({
        id: contactId,
        data: {
          targetAccountId: Number(values.targetAccountId),
          replacementPrimaryContactId: values.replacementPrimaryContactId
            ? Number(values.replacementPrimaryContactId)
            : undefined,
          replacementBillingContactId: values.replacementBillingContactId
            ? Number(values.replacementBillingContactId)
            : undefined,
        },
      });
      toast({ title: "Member reassigned to the new account." });
      invalidateProfileQueries(result.oldAccountId, result.newAccountId);
      onClose();
      // The profile route is contactId-keyed and unchanged by a reassign —
      // navigate there to force a fresh read of the now-moved account/plan
      // data (matches the interfaces contract's "close + navigate" step).
      navigate(`/members/${contactId}`);
    } catch (err) {
      // customFetch's ApiError carries the parsed JSON body on .data — the
      // .message is only "HTTP 409 Conflict", so read .data.code (same
      // pattern as accounts/contacts-list.tsx's confirmDelete).
      const data = (err as { data?: ReassignApiErrorData })?.data;
      const code = data?.code ?? "";

      if (code === "pending_dues") {
        const amount = data?.amount != null ? Number(data.amount).toFixed(2) : "0.00";
        toast({
          title: `Settle ₹${amount} in dues before reassigning.`,
          description: data?.oldestDueDate
            ? `Oldest pending period started ${data.oldestDueDate}.`
            : undefined,
          variant: "destructive",
        });
        return; // keep the dialog open
      }
      if (code === "replacement_contact_required") {
        toast({
          title: "Select a replacement Primary and Billing contact on the old account.",
          variant: "destructive",
        });
        return;
      }
      if (code === "no_eligible_replacement") {
        toast({
          title: "This account has no other contact to take over Primary/Billing.",
          description: "The move can't proceed until another contact exists on the old account.",
          variant: "destructive",
        });
        return;
      }
      if (code === "target_same_as_current") {
        toast({
          title: "Pick a different account to move this member to.",
          variant: "destructive",
        });
        return;
      }
      if (code === "target_not_in_space") {
        toast({ title: "That account isn't in the same space.", variant: "destructive" });
        return;
      }
      toast({ title: "Reassign failed. Please try again.", variant: "destructive" });
      // keep the dialog open on any unhandled error
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" />
            Reassign to another account
          </DialogTitle>
          <DialogDescription>
            Moves this member (and their active plans) to a different account.
            Already-issued invoices/receipts and past attendance stay on the current account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="targetAccountId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Target account <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={accountsLoading ? "Loading accounts…" : "Select an account"}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {targetOptions.map((a: Account) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {a.legalName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {requiresReplacement && (
              <div className="space-y-3 rounded-md border bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground">
                  This member currently holds Primary and/or Billing on this account. The old
                  account must be left with both a Primary and a Billing contact — choose
                  replacements below (they may be the same person).
                </p>

                {contactsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ) : noEligibleReplacement ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Can&apos;t reassign this member</AlertTitle>
                    <AlertDescription>
                      There&apos;s no other contact on this account to take over Primary/Billing.
                      Add another contact to the old account first — this member can&apos;t be
                      moved until then.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <FormField
                      name="replacementPrimaryContactId"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Replacement Primary <span className="text-destructive">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a contact" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {eligibleReplacements.map((c: Contact) => (
                                <SelectItem key={c.id} value={String(c.id)}>
                                  {c.fullName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="replacementBillingContactId"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Replacement Billing <span className="text-destructive">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a contact" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {eligibleReplacements.map((c: Contact) => (
                                <SelectItem key={c.id} value={String(c.id)}>
                                  {c.fullName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || reassign.isPending}>
                {reassign.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Reassign
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
